import React, { useState, useEffect } from 'react'
import '@/screens/Modals/FragmentMintModal.css'
import { Fragment } from 'common/schema/users/fragments'
import { Package } from 'lucide-react'

interface MintTier {
  name: string
  fragments: number
  color: string
}

const MINT_TIERS: MintTier[] = [
  { name: 'Bronze', fragments: 5, color: '#CD7F32' },
  { name: 'Silver', fragments: 10, color: '#C0C0C0' },
  { name: 'Gold', fragments: 20, color: '#FFD700' },
  { name: 'Diamond', fragments: 50, color: '#B9F2FF' },
]

interface FragmentMintModalProps {
  fragment: Fragment
  isOpen: boolean
  onClose: () => void
  onMint: (fragmentCount: number) => Promise<void>
}

export const FragmentMintModal: React.FC<FragmentMintModalProps> = ({
  fragment,
  isOpen,
  onClose,
  onMint,
}) => {
  const [selectedTier, setSelectedTier] = useState<MintTier | null>(null)
  const [isMinting, setIsMinting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setSelectedTier(null)
      setError(null)
      setIsMinting(false)
    }
  }, [isOpen])

  const handleMint = async () => {
    if (!selectedTier) return

    if (fragment.fragmentCount < selectedTier.fragments) {
      setError(
        `You need ${selectedTier.fragments} fragments to mint a ${selectedTier.name} NFT`
      )
      return
    }

    try {
      setIsMinting(true)
      setError(null)
      await onMint(selectedTier.fragments)
      onClose()
    } catch (err) {
      setError('Failed to mint NFT. Please try again.')
      console.error(err)
    } finally {
      setIsMinting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fmodal-overlay" onClick={onClose}>
      <div className="fmodal-content" onClick={(e) => e.stopPropagation()}>
        <div className="fmodal-header">
          <h2 className="fmodal-title">Mint NFT</h2>
          <button className="fmodal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="fmodal-fragment-info">
          <img
            src={fragment.url}
            alt={fragment.name}
            className="fmodal-fragment-image"
          />
          <div className="fmodal-fragment-details">
            <h3>{fragment.name}</h3>
            <p className="fmodal-fragment-count">
              You have: {fragment.fragmentCount} fragments
            </p>
          </div>
        </div>

        <div className="fmodal-tiers">
          {MINT_TIERS.map((tier) => (
            <div
              key={tier.name}
              className={`fmodal-tier ${selectedTier?.name === tier.name ? 'selected' : ''} ${
                fragment.fragmentCount < tier.fragments ? 'disabled' : ''
              }`}
              onClick={() =>
                fragment.fragmentCount >= tier.fragments &&
                setSelectedTier(tier)
              }
              style={{ '--tier-color': tier.color } as React.CSSProperties}
            >
              <div className="fmodal-tier-header">
                <span className="fmodal-tier-name">{tier.name}</span>
                <span className="fmodal-tier-cost">
                  {tier.fragments} fragments
                </span>
              </div>
              {fragment.fragmentCount < tier.fragments && (
                <div className="fmodal-tier-locked">
                  <Package size={20} />
                  <span>
                    Need {tier.fragments - fragment.fragmentCount} more
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {error && <div className="fmodal-error">{error}</div>}

        <button
          className={`fmodal-mint-button ${isMinting ? 'minting' : ''} ${
            !selectedTier ? 'disabled' : ''
          }`}
          onClick={handleMint}
          disabled={!selectedTier || isMinting}
        >
          {isMinting ? (
            <>
              <div className="fmodal-spinner"></div>
              Minting...
            </>
          ) : (
            `Mint ${selectedTier ? selectedTier.name : 'NFT'}`
          )}
        </button>
      </div>
    </div>
  )
}
